import { twMerge } from '@shared/themes';
import { cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import type { ParticipationState } from '../../../../lottery-purchases/types';
import { getParticipationStatusIcon } from '../../../utils/getParticipationStatusIcon/getParticipationStatusIcon';

type DrawSelectorState = 'selected' | 'disabled' | 'default';

const drawSelectorButtonVariants = cva(
  'w-full flex-grow flex items-center justify-center gap-x-4 bg-background border-1 border-border rounded-lg whitespace-nowrap outline-none focus-visible:ring-[3px] focus-visible:ring-primary-foreground transition-all duration-300',
  {
    variants: {
      state: {
        selected: 'bg-container-active border-container-active text-foreground-inverse',
        disabled: 'bg-container-base cursor-not-allowed pointer-events-none',
        default: 'hover:border-border-active',
      },
    },
  }
);

/**
 * DrawSelectorButton is extending HTMLButtonElement and adds additional styling based on the state.
 * Should be used as building block for DrawSelectorItem.
 *
 * @param state - The state of the button.
 * @returns A button element with the additional styling based on the state.
 */
function DrawSelectorButton({ className, state, ...props }: ComponentProps<'button'> & { state: DrawSelectorState }) {
  const isDisabled = state === 'disabled';
  return <button className={twMerge(drawSelectorButtonVariants({ state, className }))} disabled={isDisabled} aria-current={state === 'selected' ? true : undefined} {...props} />;
}

/**
 * DrawSelectorDateContainer is a span element container with styling expected by DrawSelectorItem.
 * Should be used as building block for DrawSelectorItem.
 *
 * @param className - The class name to apply to the span.
 * @returns A span element with the additional styling based on the class name.
 */
function DrawSelectorDateContainer({ className, ...props }: ComponentProps<'span'>) {
  return <span className={twMerge('flex items-center justify-center gap-x-1 -gap-y-0.5', className)} {...props} />;
}

/**
 * DrawSelectorText is a span element that will style text inside DrawSelectorButton. Two sizes are supported: large (default) and small.
 * Should be used as building block for DrawSelectorItem.
 *
 * @param size - The size of the text.
 * @returns A span element with the additional styling based on the size.
 */
function DrawSelectorText({ size = 'large', ...props }: ComponentProps<'span'> & { size?: 'large' | 'small' }) {
  const classes = {
    large: 'paragraph-strong',
    small: 'paragraph-s',
  };
  return <span className={classes[size]} {...props} />;
}

/**
 * DrawSelectorIcon is a div element that displays the check icon in the top right corner of the DrawSelectorButton.
 * Should be used as building block for DrawSelectorItem.
 *
 * @param className - The class name to apply to the div.
 * @returns A div element with the additional styling based on the class name.
 */
function DrawSelectorIcon({ className, participationState, ...props }: ComponentProps<'div'> & { participationState: ParticipationState }) {
  const { icon } = getParticipationStatusIcon(participationState, 'tiny');
  return (
    <div className={twMerge('size-5 absolute -top-1 -right-1 bg-background flex items-center justify-center', className)} {...props}>
      {icon}
    </div>
  );
}

export { DrawSelectorButton, DrawSelectorDateContainer, DrawSelectorIcon, DrawSelectorText };
export type { DrawSelectorState };
